import React, { BaseSyntheticEvent, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import { TaxonomySummary } from 'src/shared/types';
import { iucnStatusData } from 'src/shared/helpers/iucnStatusHelper';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import SpeciesInfoPopup from 'src/shared/components/species-info-popup/SpeciesInfoPopup';
import { Popover, PopoverAnchor, PopoverContent } from 'src/shared/components/ui/popover';
import { SecondaryButton } from 'src/shared/components/button/Button';
import Table from 'src/shared/components/table/Table';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useAppDispatch } from 'src/store';
import { updateFilters } from 'src/app/maps/views/species-map/state/speciesMapSlice';
import { SamplingEventFilterData } from 'src/shared/hooks/useProjectFilters';
import Show from 'src/shared/components/show/Show';
import { Info as InfoOutlinedIcon } from 'lucide-react';
import styles from './IucnTable.module.scss';

interface IucnTableProps {
    taxonomySummary: TaxonomySummary;
    title: string;
    helpArticleSlug: string;
    type: string;
    samplingEvent: SamplingEventFilterData;
}

const IucnTable = ({ taxonomySummary, title, helpArticleSlug, type, samplingEvent }: IucnTableProps) => {
    const testId = title.toLocaleLowerCase().replace(/\s+/g, '-');
    const [openedPopoverId, setOpenedPopoverId] = useState('');
    const dispatch = useAppDispatch();
    const navigate = useAppNavigation();

    const handlePopoverOpen = (e: BaseSyntheticEvent, popoverId: string) => {
        setOpenedPopoverId(popoverId);
    };

    const handlePopoverClose = () => {
        setOpenedPopoverId('');
    };

    const handleShowOnMap = (scientificName: string) => {
        dispatch(
            updateFilters({
                speciesHighlight: scientificName,
                samplingEvent: samplingEvent,
            })
        );
        navigate.toSpeciesMap();
    };

    const isRedLists = type === 'redLists';
    const containerId = isRedLists ? 'iucn-red-list-species-table' : 'invasive-species-table';
    return (
        <div className={styles.iucnTable} id={containerId} data-testid={containerId}>
            <div className={styles.logoContainer}>
                <Show when={isRedLists} fallback={<InfoOutlinedIcon className={`${styles.logo} ${styles.inverted}`} />}>
                    <img src={IUCNLogo} alt='' className={styles.logo} />
                </Show>

                <span className={styles.title}>
                    {title}
                    <span className={styles.questionButton}>
                        <QuestionButtonHelp
                            type='api'
                            slug={helpArticleSlug}
                            detailedExplainerSlug={`${helpArticleSlug}-te`}
                            showHelpTitle={false}
                        />
                    </span>
                </span>
            </div>
            <Table className={styles.table}>
                <thead className={styles.tableHeader}>
                    <tr>
                        <th>Species Name</th>
                        <th>Scientific Name</th>
                        <Show when={isRedLists}>
                            <th>IUCN Red List status</th>
                        </Show>
                        <th>Number of Samples</th>
                        <th>% of samples</th>
                    </tr>
                </thead>
                <tbody>
                    <Show
                        when={!isEmpty(taxonomySummary)}
                        fallback={
                            <tr>
                                <td colSpan={isRedLists ? 5 : 4} data-testid={`${testId}-no-data`}>
                                    There were no {title} detected in this dataset
                                </td>
                            </tr>
                        }
                    >
                        {taxonomySummary.map((entry, index: number) => (
                            <React.Fragment key={entry.taxonomy.tax_species}>
                                <tr onMouseEnter={e => handlePopoverOpen(e, `pop-${index}`)} onMouseLeave={handlePopoverClose}>
                                    <td>
                                        <Popover open={openedPopoverId === `pop-${index}`}>
                                            <PopoverAnchor asChild>
                                                <span data-testid={`${testId}-row-common-name-${index}`}>
                                                    {entry.taxonomy.common_name || '-'}
                                                </span>
                                            </PopoverAnchor>
                                            {entry.taxonomy.tax_species && (
                                                <PopoverContent side='right' align='start' sideOffset={8} className='z-50'>
                                                    <SpeciesInfoPopup
                                                        scientificName={entry.taxonomy.tax_species}
                                                        taxonomySummary={taxonomySummary}
                                                        bottomActions={
                                                            <SecondaryButton
                                                                onClick={() => handleShowOnMap(entry.taxonomy.tax_species as string)}
                                                            >
                                                                Show on map
                                                            </SecondaryButton>
                                                        }
                                                    />
                                                </PopoverContent>
                                            )}
                                        </Popover>
                                    </td>
                                    <td>
                                        <span data-testid={`${testId}-row-species-name-${index}`} className='italic'>
                                            {entry.taxonomy.tax_species}
                                        </span>
                                    </td>
                                    {type === 'redLists' && (
                                        <td>
                                            <span data-testid={`${testId}-row-redlist-status-${index}`}>
                                                {iucnStatusData[entry.conservationStatus.iucnRedListStatus].label}
                                            </span>
                                        </td>
                                    )}
                                    <td>
                                        <span data-testid={`${testId}-row-sample-count-${index}`}>{entry.sampleCount}</span>
                                    </td>
                                    <td>
                                        <span data-testid={`${testId}-row-samples-percent-${index}`}>
                                            {Math.round(entry.percentTotalSamples)}
                                        </span>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </Show>
                </tbody>
            </Table>
        </div>
    );
};

export default IucnTable;
